<template>
  <v-container fluid class="pa-4 pa-md-6">

    <!-- ============================================= -->
    <!-- HEADER -->
    <!-- ============================================= -->
    <v-card rounded="xl" elevation="1" class="pa-5 mb-6">
      <div class="d-flex align-start justify-space-between flex-wrap ga-4">
        <div style="flex: 1; min-width: 240px">
          <div class="d-flex align-center ga-3 mb-1">
            <v-icon icon="mdi-power" size="28" color="primary"/>
            <span class="text-h5 font-weight-bold">{{ outage.code }}</span>
          </div>
          <div class="text-body-1 text-medium-emphasis mb-4">{{ outage.name }}</div>

          <div class="d-flex flex-column ga-2 text-body-2">
            <div class="d-flex align-center ga-2">
              <v-icon size="16" icon="mdi-calendar-range"/>
              <span>{{ formatDate(outage.scheduled_start) }}</span>
              <v-icon size="14" icon="mdi-arrow-right"/>
              <span>{{ formatDate(outage.scheduled_end) }}</span>
            </div>
            <div class="d-flex align-center ga-2">
              <v-icon size="16" icon="mdi-clock-outline"/>
              <span>{{ outage.planned_hours }}h geplant</span>
            </div>
            <div v-if="outage.description" class="d-flex align-start ga-2 mt-1">
              <v-icon size="16" icon="mdi-text" class="mt-1"/>
              <span class="text-medium-emphasis">{{ outage.description }}</span>
            </div>
          </div>
        </div>

        <!-- Gesamtfortschritt -->
        <div style="min-width: 200px; max-width: 300px; flex: 0 1 auto" class="text-center">
          <div class="text-caption text-medium-emphasis mb-1">Gesamtfortschritt</div>
          <div class="text-h4 font-weight-bold" :style="{ color: progressColor }">
            {{ totalProgress }}%
          </div>
          <v-progress-linear
              :model-value="totalProgress"
              :color="progressColor"
              height="8"
              rounded
              class="mt-2"
          />
          <div class="d-flex justify-space-between text-caption text-medium-emphasis mt-1">
            <span>{{ completedTasks }} erledigt</span>
            <span>{{ totalTasks }} gesamt</span>
          </div>
        </div>
      </div>
    </v-card>

    <!-- ============================================= -->
    <!-- 3 PIE CHARTS -->
    <!-- ============================================= -->
    <v-row class="mb-6">
      <!-- Aufträge -->
      <v-col cols="12" md="4">
        <v-card rounded="xl" elevation="1" class="pa-5 h-100">
          <div class="d-flex align-center ga-2 mb-4">
            <v-icon icon="mdi-clipboard-text-multiple-outline" size="20" color="primary"/>
            <span class="text-subtitle-1 font-weight-bold">Aufträge</span>
            <v-spacer/>
            <v-chip size="small" variant="tonal" color="primary">{{ orders.length }}</v-chip>
          </div>

          <v-pie
              :items="orderChartItems"
              :size="chartSize"
              :legend="legendConfig"
              inner-cut="65"
              gap="2"
              rounded="2"
              item-key="id"
              hide-slice
              animation
              reveal
              tooltip
              class="justify-center pie-clickable"
              @click="onPieClick($event, 'orders', orderChartItems)"
          >
            <template v-slot:center>
              <div class="text-center" style="line-height: 1.25; user-select: none">
                <div class="text-h5 font-weight-bold">{{ orders.length }}</div>
                <div class="opacity-60 text-caption">Aufträge</div>
              </div>
            </template>

            <template v-slot:legend="{ items }">
              <v-list class="py-0 bg-transparent" density="compact">
                <v-list-item
                    v-for="item in items"
                    :key="item.key"
                    :title="item.title"
                    :subtitle="`${item.value} Auftr.`"
                    rounded="lg"
                    link
                    slim
                    @click="filterByState('orders', item.key)"
                >
                  <template v-slot:prepend>
                    <v-avatar :color="item.color" :size="12" class="me-2"></v-avatar>
                  </template>
                  <template v-slot:append>
                    <v-icon size="14" icon="mdi-arrow-right" class="opacity-40"/>
                  </template>
                </v-list-item>
              </v-list>
            </template>
          </v-pie>
        </v-card>
      </v-col>

      <!-- Aufgaben -->
      <v-col cols="12" md="4">
        <v-card rounded="xl" elevation="1" class="pa-5 h-100">
          <div class="d-flex align-center ga-2 mb-4">
            <v-icon icon="mdi-wrench-outline" size="20" color="primary"/>
            <span class="text-subtitle-1 font-weight-bold">Aufgaben</span>
            <v-spacer/>
            <v-chip size="small" variant="tonal" color="primary">{{ tasks.length }}</v-chip>
          </div>

          <v-pie
              :items="taskChartItems"
              :size="chartSize"
              :legend="legendConfig"
              inner-cut="65"
              gap="2"
              rounded="2"
              item-key="id"
              hide-slice
              animation
              reveal
              tooltip
              class="justify-center pie-clickable"
              @click="onPieClick($event, 'tasks', taskChartItems)"
          >
            <template v-slot:center>
              <div class="text-center" style="line-height: 1.25; user-select: none">
                <div class="text-h5 font-weight-bold">{{ tasks.length }}</div>
                <div class="opacity-60 text-caption">Aufgaben</div>
              </div>
            </template>

            <template v-slot:legend="{ items }">
              <v-list class="py-0 bg-transparent" density="compact">
                <v-list-item
                    v-for="item in items"
                    :key="item.key"
                    :title="item.title"
                    :subtitle="`${item.value} Aufg.`"
                    rounded="lg"
                    link
                    slim
                    @click="filterByState('tasks', item.key)"
                >
                  <template v-slot:prepend>
                    <v-avatar :color="item.color" :size="12" class="me-2"></v-avatar>
                  </template>
                  <template v-slot:append>
                    <v-icon size="14" icon="mdi-arrow-right" class="opacity-40"/>
                  </template>
                </v-list-item>
              </v-list>
            </template>
          </v-pie>
        </v-card>
      </v-col>

      <!-- Checklisten -->
      <v-col cols="12" md="4">
        <v-card rounded="xl" elevation="1" class="pa-5 h-100">
          <div class="d-flex align-center ga-2 mb-4">
            <v-icon icon="mdi-format-list-checks" size="20" color="primary"/>
            <span class="text-subtitle-1 font-weight-bold">Checklisten</span>
            <v-spacer/>
            <v-chip size="small" variant="tonal" color="primary">{{ checklists.length }}</v-chip>
          </div>

          <v-pie
              :items="checklistChartItems"
              :size="chartSize"
              :legend="legendConfig"
              inner-cut="65"
              gap="2"
              rounded="2"
              item-key="id"
              hide-slice
              animation
              reveal
              tooltip
              class="justify-center pie-clickable"
              @click="onPieClick($event, 'checklists', checklistChartItems)"
          >
            <template v-slot:center>
              <div class="text-center" style="line-height: 1.25; user-select: none">
                <div class="text-h5 font-weight-bold">{{ checklists.length }}</div>
                <div class="opacity-60 text-caption">Checkl.</div>
              </div>
            </template>

            <template v-slot:legend="{ items }">
              <v-list class="py-0 bg-transparent" density="compact">
                <v-list-item
                    v-for="item in items"
                    :key="item.key"
                    :title="item.title"
                    :subtitle="`${item.value} Checkl.`"
                    rounded="lg"
                    link
                    slim
                    @click="filterByState('checklists', item.key)"
                >
                  <template v-slot:prepend>
                    <v-avatar :color="item.color" :size="12" class="me-2"></v-avatar>
                  </template>
                  <template v-slot:append>
                    <v-icon size="14" icon="mdi-arrow-right" class="opacity-40"/>
                  </template>
                </v-list-item>
              </v-list>
            </template>
          </v-pie>
        </v-card>
      </v-col>
    </v-row>

    <!-- ============================================= -->
    <!-- DETAILLISTEN (Tabs) -->
    <!-- ============================================= -->
    <v-card ref="detailCard" rounded="xl" elevation="1">
      <v-tabs v-model="activeTab" color="primary" grow>
        <v-tab value="orders">
          <v-icon start icon="mdi-clipboard-text-multiple-outline" size="18"/>
          Aufträge ({{ filteredOrders.length }}<span v-if="stateFilter" class="text-medium-emphasis">/{{ orders.length }}</span>)
        </v-tab>
        <v-tab value="tasks">
          <v-icon start icon="mdi-wrench-outline" size="18"/>
          Aufgaben ({{ filteredTasks.length }}<span v-if="stateFilter" class="text-medium-emphasis">/{{ tasks.length }}</span>)
        </v-tab>
        <v-tab value="checklists">
          <v-icon start icon="mdi-format-list-checks" size="18"/>
          Checklisten ({{ filteredChecklists.length }}<span v-if="stateFilter" class="text-medium-emphasis">/{{ checklists.length }}</span>)
        </v-tab>
      </v-tabs>

      <!-- Filter Indicator -->
      <div v-if="stateFilter" class="pa-3 d-flex align-center ga-2" style="background: rgba(var(--v-theme-on-surface), 0.03)">
        <v-icon size="16" icon="mdi-filter" class="text-medium-emphasis"/>
        <span class="text-body-2 text-medium-emphasis">Gefiltert nach:</span>
        <v-chip
            size="small"
            :color="StateColor[stateFilter as State]"
            variant="flat"
            label
            closable
            @click:close="clearFilter"
        >
          {{ StateDescription[stateFilter as State] ?? stateFilter }}
        </v-chip>
      </div>

      <v-divider/>

      <v-tabs-window v-model="activeTab">
        <!-- ===== AUFTRÄGE TAB ===== -->
        <v-tabs-window-item value="orders">
          <v-table hover density="comfortable" class="detail-table">
            <thead>
            <tr>
              <th>ID</th>
              <th>Titel</th>
              <th>Gruppe</th>
              <th>Status</th>
              <th>Priorität</th>
              <th>Fortschritt</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="order in filteredOrders" :key="order.id" class="cursor-pointer">
              <td>
                <v-chip size="small" label border="thin opacity-25" prepend-icon="mdi-clipboard-text-outline">
                  {{ order.id }}
                </v-chip>
              </td>
              <td>{{ order.title }}</td>
              <td>
                <v-chip size="small" variant="tonal">{{ order.work_group }}</v-chip>
              </td>
              <td>
                <v-chip
                    size="small"
                    :color="StateColor[order.state as State]"
                    variant="flat"
                    label
                >
                  {{ StateDescription[order.state as State] ?? order.state }}
                </v-chip>
              </td>
              <td class="text-center">
                <v-chip size="small" :color="getPriorityColor(order.priority)" variant="tonal">
                  P{{ order.priority }}
                </v-chip>
              </td>
              <td style="min-width: 120px">
                <div class="d-flex align-center ga-2">
                  <v-progress-linear
                      :model-value="order.progress"
                      :color="order.progress === 100 ? '#4CAF50' : '#1976D2'"
                      height="6"
                      rounded
                      style="flex: 1"
                  />
                  <span class="text-caption" style="min-width: 32px">{{ order.progress }}%</span>
                </div>
              </td>
            </tr>
            <tr v-if="filteredOrders.length === 0">
              <td colspan="6" class="text-center text-medium-emphasis pa-6">
                Keine Aufträge mit Status „{{ StateDescription[stateFilter as State] ?? stateFilter }}"
              </td>
            </tr>
            </tbody>
          </v-table>
        </v-tabs-window-item>

        <!-- ===== AUFGABEN TAB ===== -->
        <v-tabs-window-item value="tasks">
          <v-table hover density="comfortable" class="detail-table">
            <thead>
            <tr>
              <th>Auftr.</th>
              <th>ID</th>
              <th>Titel</th>
              <th>Zugewiesen</th>
              <th>Status</th>
              <th>Dauer</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="task in filteredTasks" :key="`${task.order_id}-${task.id}`" class="cursor-pointer">
              <td class="text-medium-emphasis">{{ task.order_id }}</td>
              <td>
                <v-chip size="small" label border="thin opacity-25" prepend-icon="mdi-wrench-outline">
                  {{ task.id }}
                </v-chip>
              </td>
              <td>
                <div>{{ task.title }}</div>
                <div v-if="task.is_lockout" class="text-caption text-warning">
                  <v-icon size="12" icon="mdi-lightning-bolt"/> Freischaltung
                </div>
              </td>
              <td>
                <v-chip v-if="task.assignee" size="small" variant="tonal" prepend-icon="mdi-account">
                  {{ task.assignee }}
                </v-chip>
                <span v-else class="text-medium-emphasis">—</span>
              </td>
              <td>
                <v-chip
                    size="small"
                    :color="StateColor[task.state as State]"
                    variant="flat"
                    label
                >
                  {{ StateDescription[task.state as State] ?? task.state }}
                </v-chip>
              </td>
              <td class="text-medium-emphasis">{{ task.scheduled_duration ?? '—' }}</td>
            </tr>
            <tr v-if="filteredTasks.length === 0">
              <td colspan="6" class="text-center text-medium-emphasis pa-6">
                Keine Aufgaben mit Status „{{ StateDescription[stateFilter as State] ?? stateFilter }}"
              </td>
            </tr>
            </tbody>
          </v-table>
        </v-tabs-window-item>

        <!-- ===== CHECKLISTEN TAB ===== -->
        <v-tabs-window-item value="checklists">
          <v-table hover density="comfortable" class="detail-table">
            <thead>
            <tr>
              <th>ID</th>
              <th>Titel</th>
              <th>Zugewiesen</th>
              <th>Status</th>
              <th>Fortschritt</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="cl in filteredChecklists" :key="cl.id" class="cursor-pointer">
              <td>
                <v-chip size="small" label border="thin opacity-25" prepend-icon="mdi-format-list-checks">
                  {{ cl.id }}
                </v-chip>
              </td>
              <td>{{ cl.title }}</td>
              <td>
                <div class="d-flex ga-1 flex-wrap">
                  <v-chip
                      v-for="a in cl.assignees"
                      :key="a"
                      size="small"
                      variant="tonal"
                      prepend-icon="mdi-account"
                  >
                    {{ a }}
                  </v-chip>
                </div>
              </td>
              <td>
                <v-chip
                    size="small"
                    :color="StateColor[cl.state as State]"
                    variant="flat"
                    label
                >
                  {{ StateDescription[cl.state as State] ?? cl.state }}
                </v-chip>
              </td>
              <td style="min-width: 120px">
                <div class="d-flex align-center ga-2">
                  <v-progress-linear
                      :model-value="cl.progress"
                      :color="cl.progress === 100 ? '#4CAF50' : '#1976D2'"
                      height="6"
                      rounded
                      style="flex: 1"
                  />
                  <span class="text-caption" style="min-width: 48px">{{ cl.checked }}/{{ cl.total }}</span>
                </div>
              </td>
            </tr>
            <tr v-if="filteredChecklists.length === 0">
              <td colspan="5" class="text-center text-medium-emphasis pa-6">
                Keine Checklisten mit Status „{{ StateDescription[stateFilter as State] ?? stateFilter }}"
              </td>
            </tr>
            </tbody>
          </v-table>
        </v-tabs-window-item>
      </v-tabs-window>
    </v-card>

  </v-container>

  <!-- SVG Pattern für Donut Hatching -->
  <div class="h-0">
    <svg height="0" version="1.1" width="0" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern
            id="pattern-0"
            height="20"
            patternTransform="rotate(145) scale(.2)"
            patternUnits="userSpaceOnUse"
            width="20"
        >
          <path d="M0 10h20zm0 20h20zm0 20h20zm0 20h20z" fill="none" stroke="rgb(var(--v-theme-surface))" stroke-width="3"/>
        </pattern>
      </defs>
    </svg>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, ref} from 'vue'
import {useDisplay} from 'vuetify'
import {State, StateColor, StateDescription} from '@/types/Order'

const display = useDisplay()
const activeTab = ref('orders')
const stateFilter = ref<string | null>(null)
const detailCard = ref<HTMLElement | null>(null)

const chartSize = computed(() => display.smAndDown.value ? 200 : 240)
const legendConfig = computed(() => ({
  position: display.mdAndUp.value ? 'bottom' : 'bottom'
}))

// =====================================================
// DUMMY DATEN — Stillstand 2612
// =====================================================

const outage = {
  code: '2612',
  name: 'Jahresstillstand PM3 Sommer 2025',
  description: 'Großer geplanter Stillstand für Revision und Wartungsarbeiten an PM3. Schwerpunkte: Trockenpartie, Pressenpartie und Antriebe.',
  scheduled_start: '2025-07-06T06:00:00',
  scheduled_end: '2025-11-22T18:00:00',
  planned_hours: 37,
}

const orders = [
  {id: 4001, title: 'Pressenwalze tauschen', work_group: 'TDM-PM3', state: 'BEEND', priority: '2', progress: 100},
  {id: 4002, title: 'Trockengruppe 3 — Lager prüfen', work_group: 'TDM-PM3', state: 'INBEARB', priority: '2', progress: 60},
  {id: 4003, title: 'E-Antriebe Sektion 5 Revision', work_group: 'TDE-PM3', state: 'TERM', priority: '1', progress: 0},
  {id: 4004, title: 'SPS-Programm Update Rollenwickler', work_group: 'TDE-PM3', state: 'BEEND', priority: '3', progress: 100},
  {id: 4005, title: 'Dampfleitung Isolation erneuern', work_group: 'TDM-PM3', state: 'ANFO', priority: '3', progress: 0},
]

const tasks = [
  {order_id: 4001, id: 1, title: 'Alte Walze demontieren', assignee: 'beckm', state: 'BEEND', is_lockout: false, scheduled_duration: '4h'},
  {order_id: 4001, id: 2, title: 'Freischaltung Pressenpartie', assignee: 'weberk', state: 'BEEND', is_lockout: true, scheduled_duration: '1h'},
  {order_id: 4001, id: 3, title: 'Neue Walze einbauen', assignee: 'beckm', state: 'BEEND', is_lockout: false, scheduled_duration: '6h'},
  {order_id: 4001, id: 4, title: 'Ausrichten & Probelauf', assignee: 'beckm', state: 'BEEND', is_lockout: false, scheduled_duration: '3h'},
  {order_id: 4002, id: 1, title: 'Lagertemperatur messen', assignee: 'schmidth', state: 'BEEND', is_lockout: false, scheduled_duration: '2h'},
  {order_id: 4002, id: 2, title: 'Lagerspiel prüfen', assignee: 'schmidth', state: 'BEEND', is_lockout: false, scheduled_duration: '1.5h'},
  {order_id: 4002, id: 3, title: 'Schmierstoffwechsel', assignee: 'schmidth', state: 'INBEARB', is_lockout: false, scheduled_duration: '2h'},
  {order_id: 4002, id: 4, title: 'Prüfprotokoll erstellen', assignee: null, state: 'ANFO', is_lockout: false, scheduled_duration: '1h'},
  {order_id: 4003, id: 1, title: 'Freischaltung E-Antriebe Sek. 5', assignee: 'weberk', state: 'TERM', is_lockout: true, scheduled_duration: '1h'},
  {order_id: 4003, id: 2, title: 'Motor 5.1 ausbauen', assignee: null, state: 'ANFO', is_lockout: false, scheduled_duration: '3h'},
  {order_id: 4003, id: 3, title: 'Motor 5.2 Wicklung prüfen', assignee: null, state: 'ANFO', is_lockout: false, scheduled_duration: '2h'},
  {order_id: 4003, id: 4, title: 'Motor 5.1 einbauen & verdrahten', assignee: null, state: 'ANFO', is_lockout: false, scheduled_duration: '4h'},
  {order_id: 4003, id: 5, title: 'Schutzprüfung durchführen', assignee: null, state: 'ANFO', is_lockout: false, scheduled_duration: '1.5h'},
  {order_id: 4004, id: 1, title: 'Backup altes Programm', assignee: 'muellerj', state: 'BEEND', is_lockout: false, scheduled_duration: '0.5h'},
  {order_id: 4004, id: 2, title: 'Neues Programm aufspielen', assignee: 'muellerj', state: 'BEEND', is_lockout: false, scheduled_duration: '2h'},
  {order_id: 4004, id: 3, title: 'Funktionstest Wickler', assignee: 'muellerj', state: 'BEEND', is_lockout: false, scheduled_duration: '1.5h'},
  {order_id: 4005, id: 1, title: 'Alte Isolation entfernen', assignee: null, state: 'ANFO', is_lockout: false, scheduled_duration: '3h'},
  {order_id: 4005, id: 2, title: 'Rohrleitung prüfen', assignee: null, state: 'ANFO', is_lockout: false, scheduled_duration: '2h'},
  {order_id: 4005, id: 3, title: 'Neue Isolation anbringen', assignee: null, state: 'ANFO', is_lockout: false, scheduled_duration: '4h'},
  {order_id: 4005, id: 4, title: 'Dichtheitsprüfung', assignee: null, state: 'ANFO', is_lockout: false, scheduled_duration: '1h'},
  {order_id: 4002, id: 5, title: 'Lager nachbestellen (Ersatz)', assignee: 'schmidth', state: 'WMAT', is_lockout: false, scheduled_duration: null},
  {order_id: 4003, id: 6, title: 'Frequenzumrichter FU5.1 parametrieren', assignee: null, state: 'ANFO', is_lockout: false, scheduled_duration: '2h'},
]

const checklists = [
  {id: 1, title: 'Sicherheits-Checkliste Pressenpartie', assignees: ['weberk', 'beckm'], state: 'BEEND', checked: 8, total: 8, progress: 100},
  {id: 2, title: 'Qualitätsprüfung Trockengruppe 3', assignees: ['schmidth'], state: 'INBEARB', checked: 4, total: 10, progress: 40},
  {id: 3, title: 'Abnahmeprotokoll E-Antriebe Sek. 5', assignees: ['weberk'], state: 'ANFO', checked: 0, total: 12, progress: 0},
]

// =====================================================
// COMPUTED: Chart items
// =====================================================

function buildStateChart(items: { state: string }[]) {
  const counts: Record<string, number> = {}
  items.forEach(i => {
    counts[i.state] = (counts[i.state] || 0) + 1
  })
  return Object.entries(counts)
      .filter(([, c]) => c > 0)
      .map(([state, count]) => ({
        id: state,
        title: StateDescription[state as State] ?? state,
        value: count,
        color: StateColor[state as State] ?? '#9E9E9E',
        pattern: state === 'ANFO' ? 'url(#pattern-0)' : '',
      }))
}

const orderChartItems = computed(() => buildStateChart(orders))
const taskChartItems = computed(() => buildStateChart(tasks))
const checklistChartItems = computed(() => buildStateChart(checklists))

// =====================================================
// FILTER by State (click on chart/legend → jump to list)
// =====================================================

function filterByState(tab: string, state: string) {
  activeTab.value = tab
  stateFilter.value = state
  nextTick(() => {
    const el = (detailCard.value as any)?.$el ?? detailCard.value
    el?.scrollIntoView({behavior: 'smooth', block: 'start'})
  })
}

function clearFilter() {
  stateFilter.value = null
}

function onPieClick(event: MouseEvent, tab: string, chartItems: ReturnType<typeof buildStateChart>) {
  // Walk up to find the <g class="v-pie-segment"> wrapper
  let el = event.target as HTMLElement | null
  while (el && !el.classList?.contains('v-pie-segment')) {
    el = el.parentElement
  }
  if (!el?.parentElement) return

  // Index of this segment among siblings = index in chartItems
  const segments = Array.from(el.parentElement.children)
      .filter(c => c.classList.contains('v-pie-segment'))
  const index = segments.indexOf(el)

  if (index >= 0 && index < chartItems.length) {
    filterByState(tab, chartItems[index].id)
  }
}

const filteredOrders = computed(() =>
    stateFilter.value ? orders.filter(o => o.state === stateFilter.value) : orders
)
const filteredTasks = computed(() =>
    stateFilter.value ? tasks.filter(t => t.state === stateFilter.value) : tasks
)
const filteredChecklists = computed(() =>
    stateFilter.value ? checklists.filter(c => c.state === stateFilter.value) : checklists
)

// Gesamtfortschritt
const completedTasks = computed(() => tasks.filter(t => t.state === 'BEEND' || t.state === 'GESCHL').length)
const totalTasks = computed(() => tasks.length)
const totalProgress = computed(() => Math.round((completedTasks.value / totalTasks.value) * 100))
const progressColor = computed(() => {
  if (totalProgress.value >= 80) return '#4CAF50'
  if (totalProgress.value >= 40) return '#1976D2'
  return '#FF9800'
})

// =====================================================
// HELPER
// =====================================================

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '?'
  return new Date(dateStr).toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'})
}

function getPriorityColor(p: string): string {
  switch (p) {
    case '1': return '#D32F2F'
    case '2': return '#FF9800'
    case '3': return '#1976D2'
    case '4': return '#9E9E9E'
    default: return '#9E9E9E'
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.pie-clickable :deep(path) {
  cursor: pointer;
}

.detail-table :deep(th),
.detail-table :deep(td) {
  text-align: center !important;
  vertical-align: middle !important;
}
</style>
